import { apiCreateInspection } from "@app/api/inspections/inspections.api";
import {
  ItemCodeEnum,
  ItemResultType,
  ResultType,
  UploadFileType,
} from "@app/constants";
import { handleUploadStore } from "@app/helpers";

export type CreateInspectionItemDraft = {
  resultValue: string;
  numericValue: string;
  comment: string;
  selectedFreeText: string;
  mediaFiles: File[];
  recordedBlob: Blob | null;
};

export type CreateInspectionTemplateItemOption = {
  name: string;
  position?: number;
  customInspectionItemsOptionsId?: string;
};

export type CreateInspectionTemplateItem = {
  inspectionItemId: string;
  name: string;
  resultType: string;
  itemCode: string | null;
  isRequired: boolean;
  options?: CreateInspectionTemplateItemOption[];
};

export type MachineReportMediaPayload = {
  fileName: string;
  mediaUrl: string;
  filePath: string;
  mimeType: string;
  captureMediaAt: string;
};

export type MachineReportRecordingPayload = {
  fileName: string;
  recordingUrl: string;
  filePath: string;
  mimeType: string;
  recordingAt: string;
};

export type MachineReportPayload = {
  reportTitle: string;
  reportComment: string;
  machineReportMedias: MachineReportMediaPayload[];
  machineReportRecordings: MachineReportRecordingPayload[];
  signatureUrl?: string;
  lastUpdatedSignatureUrl?: string;
};

export type CreateInspectionItemPayload = {
  inspectionItemId: string;
  result: string;
  isRequired: boolean;
  resultType: string;
  itemCode: string | null;
  freeText?: string;
  machineReport: MachineReportPayload | null;
};

export type CreateInspectionPayload = {
  inspectionFormId: string;
  currentStatus: string;
  signatureUrl?: string;
  inspectionAt?: string;
  inspectionItems: CreateInspectionItemPayload[];
};

const NUMERIC_SMR_MAX = 999999.9;
const NUMERIC_ODO_MAX = 999999.9;

function isNumericItem(item: CreateInspectionTemplateItem): boolean {
  return (
    item.resultType === ResultType.NUMERIC ||
    item.resultType === ResultType.ODOMETER
  );
}

function getNumericMax(item: CreateInspectionTemplateItem): number {
  return item.itemCode === ItemCodeEnum.ODOMETER ||
    item.resultType === ResultType.ODOMETER
    ? NUMERIC_ODO_MAX
    : NUMERIC_SMR_MAX;
}

function stripLeadingZerosFromNumeric(value: string): string {
  if (!value) return value;
  if (value.startsWith("0.")) return value;

  if (value.includes(".")) {
    const [intPart, ...decParts] = value.split(".");
    const decPart = decParts.join("").replace(/\./g, "");
    const strippedInt = intPart.replace(/^0+/, "") || "0";
    if (value.endsWith(".") && decPart === "") {
      return `${strippedInt}.`;
    }
    return `${strippedInt}.${decPart}`;
  }

  return value.replace(/^0+/, "") || "0";
}

function enforceNumericDecimalFormat(value: string): string {
  const dotIndex = value.indexOf(".");
  if (dotIndex === -1) return value;

  const intPart = value.slice(0, dotIndex);
  const decPart = value.slice(dotIndex + 1).replace(/\./g, "");
  if (value.endsWith(".") && decPart === "") {
    return `${intPart}.`;
  }
  return `${intPart}.${decPart.slice(0, 1)}`;
}

export function normalizeNumericResultForPayload(
  value: string,
  item: CreateInspectionTemplateItem,
): string {
  const trimmed = value.trim();
  if (!trimmed) {
    // return "0.0";
    return "";
  }

  let normalized = enforceNumericDecimalFormat(
    stripLeadingZerosFromNumeric(trimmed),
  );
  if (/^\d+$/.test(normalized)) {
    normalized = `${normalized}.0`;
  } else if (normalized.endsWith(".")) {
    normalized = `${normalized}0`;
  }

  const parsed = parseFloat(normalized);
  const max = getNumericMax(item);
  if (Number.isFinite(parsed) && parsed > max) {
    return String(max);
  }

  return normalized;
}

export function resolveItemResult(
  item: CreateInspectionTemplateItem,
  draft: CreateInspectionItemDraft | undefined,
): string {
  if (isNumericItem(item)) {
    return normalizeNumericResultForPayload(draft?.numericValue ?? "", item);
  }

  if (item.resultType === ResultType.FREE_TEXT) {
    return ResultType.FREE_TEXT;
  }

  if (!draft) return "";

  if (item.resultType === ResultType.NONE) {
    return ItemResultType.NONE;
  }

  return draft.resultValue || "";
}

export function shouldIncludeMachineReport(
  medias: MachineReportMediaPayload[],
  recordings: MachineReportRecordingPayload[],
  comment: string,
  result: string,
  resultType: string,
): boolean {
  if (resultType === ResultType.FREE_TEXT) {
    return medias.length > 0 || recordings.length > 0 || comment.length > 0;
  }

  return (
    medias.length > 0 ||
    recordings.length > 0 ||
    comment.length > 0 ||
    (result !== ItemResultType.OK &&
      result !== ItemResultType.NOT_APPLICABLE &&
      result !== ItemResultType.NONE &&
      result !== "")
  );
}

export async function uploadMediaFiles(
  files: File[],
): Promise<MachineReportMediaPayload[]> {
  const uploaded: MachineReportMediaPayload[] = [];

  for (const file of files) {
    if (!(file instanceof File)) continue;

    const stored = (await handleUploadStore(
      UploadFileType.MACHINE_REPORT,
      file,
    )) as (File & { filePath?: string }) | null;

    if (!stored?.filePath) continue;

    const captureAt = new Date(file.lastModified || Date.now()).toISOString();

    uploaded.push({
      fileName: stored.name || file.name,
      mediaUrl: stored.filePath,
      filePath: stored.filePath,
      mimeType: stored.type || file.type || "application/octet-stream",
      captureMediaAt: captureAt,
    });
  }

  return uploaded;
}

export async function uploadSignature(
  signatureDataUrl: string | null,
): Promise<string> {
  if (!signatureDataUrl?.startsWith("data:")) {
    throw new Error("SIGNATURE_REQUIRED");
  }

  const mimeMatch = signatureDataUrl.match(/^data:([^;]+);/);
  const mimeType = mimeMatch?.[1] ?? "image/png";
  const extension = mimeType === "image/jpeg" ? "jpg" : "png";
  const byteString = atob(signatureDataUrl.split(",")[1]);
  const buffer = new ArrayBuffer(byteString.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < byteString.length; i++) {
    bytes[i] = byteString.charCodeAt(i);
  }

  const signatureFile = new File(
    [new Blob([buffer], { type: mimeType })],
    `signature_${Date.now()}.${extension}`,
    { type: mimeType },
  );

  const stored = (await handleUploadStore(
    UploadFileType.MACHINE_REPORT,
    signatureFile,
  )) as (File & { filePath?: string }) | null;

  if (!stored?.filePath) {
    throw new Error("SIGNATURE_UPLOAD_FAILED");
  }

  return stored.filePath;
}

export async function uploadRecording(
  recordedBlob: Blob | null,
): Promise<MachineReportRecordingPayload[]> {
  if (!recordedBlob?.size) return [];

  const audioFile = new File([recordedBlob], `recording_${Date.now()}.webm`, {
    type: recordedBlob.type || "audio/webm",
  });

  const stored = (await handleUploadStore(
    UploadFileType.MACHINE_REPORT,
    audioFile,
  )) as (File & { filePath?: string }) | null;

  if (!stored?.filePath) return [];

  return [
    {
      fileName: stored.name || audioFile.name,
      filePath: stored.filePath,
      recordingUrl: stored.filePath,
      mimeType: stored.type || audioFile.type,
      recordingAt: new Date().toISOString(),
    },
  ];
}

async function buildInspectionItemPayload(
  item: CreateInspectionTemplateItem,
  draft: CreateInspectionItemDraft | undefined,
  signatureUrl?: string,
): Promise<CreateInspectionItemPayload> {
  const result = resolveItemResult(item, draft);
  const freeText =
    item.resultType === ResultType.FREE_TEXT
      ? draft?.selectedFreeText?.trim() || undefined
      : undefined;
  let machineReport: MachineReportPayload | null = null;

  if (!isNumericItem(item) && draft) {
    const machineReportMedias = await uploadMediaFiles(draft.mediaFiles);
    const machineReportRecordings = await uploadRecording(draft.recordedBlob);
    const reportComment =
      item.resultType === ResultType.FREE_TEXT && !draft.comment.trim()
        ? freeText || ""
        : draft.comment.trim();

    if (
      shouldIncludeMachineReport(
        machineReportMedias,
        machineReportRecordings,
        reportComment,
        result,
        item.resultType,
      )
    ) {
      machineReport = {
        reportTitle: item.name,
        reportComment,
        machineReportMedias,
        machineReportRecordings,
        ...(result === ItemResultType.ANOMARY && signatureUrl
          ? { signatureUrl }
          : {}),
      };
    }
  }

  return {
    inspectionItemId: item.inspectionItemId,
    result,
    isRequired: item.isRequired,
    resultType: item.resultType,
    itemCode: item.itemCode ?? null,
    ...(freeText ? { freeText } : {}),
    machineReport,
  };
}

export async function submitCreateInspection(params: {
  groupId: string;
  machineId: string;
  inspectionFormId: string;
  items: CreateInspectionTemplateItem[];
  itemDrafts: Record<string, CreateInspectionItemDraft>;
  signatureDataUrl: string | null;
  inspectionAt?: string;
}): Promise<{ successMessage?: string }> {
  const {
    groupId,
    machineId,
    inspectionFormId,
    items,
    itemDrafts,
    signatureDataUrl,
    inspectionAt,
  } = params;

  const signatureUrl = signatureDataUrl
    ? await uploadSignature(signatureDataUrl)
    : undefined;

  const inspectionItems = await Promise.all(
    items.map((item) =>
      buildInspectionItemPayload(
        item,
        itemDrafts[item.inspectionItemId],
        signatureUrl,
      ),
    ),
  );

  const payload: CreateInspectionPayload = {
    inspectionFormId,
    currentStatus: "POSTED",
    ...(signatureUrl ? { signatureUrl } : {}),
    ...(inspectionAt ? { inspectionAt } : {}),
    inspectionItems,
  };

  const response = await apiCreateInspection(groupId, machineId, payload);
  return {
    successMessage: response?.data?.meta?.successMessage,
  };
}
